import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

// In-memory cache to avoid redundant fetches across components on the same page
const cache = {};

/**
 * Custom hook to fetch dynamic page content from the backend API.
 * Merges API content with static defaults so the site never breaks
 * if no database content exists.
 *
 * @param {string} pageSlug - The page identifier (e.g., 'home', 'about', 'services')
 * @returns {{ content: Object, loading: boolean, refresh: Function }}
 *
 * Usage:
 *   const { content } = useDynamicContent('home');
 *   const title = content.hero_title || 'Default Title';
 */
export default function useDynamicContent(pageSlug) {
  const [content, setContent] = useState(cache[pageSlug] || {});
  const [loading, setLoading] = useState(!cache[pageSlug]);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!pageSlug) return;
    
    // If already cached, use it
    if (cache[pageSlug]) {
      setContent(cache[pageSlug]);
      setLoading(false);
      return;
    }

    // Prevent double-fetch in React StrictMode
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchContent = async () => {
      try {
        const res = await axios.get(`${API_URL}/pages?page_slug=${pageSlug}`);
        const contentMap = {};
        (res.data || []).forEach(item => {
          contentMap[item.section_key] = item.content;
          if (item.content_json) {
            contentMap[`${item.section_key}_json`] = item.content_json;
          }
        });
        cache[pageSlug] = contentMap;
        setContent(contentMap);
      } catch (err) {
        // Silently fail — static defaults will be used
        console.warn(`[useDynamicContent] Failed to fetch content for "${pageSlug}":`, err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [pageSlug]);

  const refresh = async () => {
    try {
      const res = await axios.get(`${API_URL}/pages?page_slug=${pageSlug}`);
      const contentMap = {};
      (res.data || []).forEach(item => {
        contentMap[item.section_key] = item.content;
        if (item.content_json) {
          contentMap[`${item.section_key}_json`] = item.content_json;
        }
      });
      cache[pageSlug] = contentMap;
      setContent(contentMap);
    } catch (err) {
      console.warn(`[useDynamicContent] Refresh failed for "${pageSlug}":`, err.message);
    }
  };

  return { content, loading, refresh };
}
